import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import type { ProjectHealth, ProjectStatus } from '../../lib/projectsApi';
import { fromDateKey } from '../../lib/calendarDates';
import { GanttChart, type GanttRow } from './GanttChart';
import { healthLabel, statusLabel } from '../projects/ProjectBadges';
import { FadeIn, SkeletonTable } from '../ui/Skeleton';
import { useI18n } from '../../contexts/I18nContext';

interface GanttProjectsProps {
  horizonStart: Date;
  weeks: number;
}

// Projects in a delivery state get a bar; finished/cancelled/lead stay out.
const VISIBLE_STATUSES: ProjectStatus[] = ['PLANNING', 'ACTIVE', 'ON_HOLD', 'ALMOST_DONE'];

// Bar color follows the project's health (mirrors HealthBadge semantics).
const HEALTH_COLORS: Record<ProjectHealth, string> = {
  ON_TRACK: 'bg-brand-600',
  AT_RISK: 'bg-amber-500',
  OFF_TRACK: 'bg-red-500',
};

/** Gantt of in-flight projects over their startDate→endDate intervals. */
export function GanttProjects({ horizonStart, weeks }: GanttProjectsProps) {
  const { t } = useI18n();
  const { data, isLoading } = useProjects({ limit: 200 });

  const projects = useMemo(
    () => (data?.projects ?? []).filter((p) => VISIBLE_STATUSES.includes(p.status)),
    [data],
  );

  const withRange = projects.filter((p) => p.startDate);
  const withoutRange = projects.filter((p) => !p.startDate);

  const rows: GanttRow[] = withRange.map((p) => ({
    id: p.id,
    label: p.name,
    labelHref: `/projects/${p.id}`,
    bars: [
      {
        id: p.id,
        start: fromDateKey(p.startDate as string),
        end: p.endDate ? fromDateKey(p.endDate) : null,
        color: HEALTH_COLORS[p.health],
        label: p.name,
        title: `${p.name}\n${statusLabel(p.status)} · ${healthLabel(p.health)}`,
      },
    ],
  }));

  if (isLoading) {
    return <SkeletonTable rows={6} />;
  }

  return (
    <FadeIn className="space-y-3">
      <GanttChart horizonStart={horizonStart} weeks={weeks} rows={rows} />
      {withoutRange.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            {t('calendar.noRange')}
          </p>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {withoutRange.map((p) => (
              <li key={p.id} className="text-sm">
                <Link to={`/projects/${p.id}`} className="text-gray-700 hover:text-brand-600">
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </FadeIn>
  );
}
